import {
  useAuth,
  TellescopeModelFromName,
  useTellescopeInfiniteQuery,
  useTellescopeMutation,
} from "@tellescope/tanstack-query";
import { parseVitalProgramConfigurationValue } from "@tellescope/utils";
import { useState } from "react";

export type VitalProgram = { title: string; configurationIds: string[] };
export type VitalPrograms = { programs: VitalProgram[] };

export type VitalsSettingsView = "settings" | "edit-programs" | "add-config" | "edit-config";

const MS_PER_MINUTE = 60_000;

const intervalToMinutes = (intervalInMS: number | undefined): string =>
  intervalInMS ? String(Math.round(intervalInMS / MS_PER_MINUTE)) : "";

export const useVitalsSettings = () => {
  const { selectedOrganization } = useAuth();
  const [view, setView] = useState<VitalsSettingsView>("settings");
  const [selectedConfig, setSelectedConfig] = useState<TellescopeModelFromName<"vital_configurations"> | null>(null);

  const org = selectedOrganization;
  const enduserSettings = org?.settings?.endusers;

  // ── Section: Interval ──────────────────────────────────
  // Initialized from org at first render; reset via key-based remount or cancel handler
  const [delayedReadingIntervalMinutes, setDelayedReadingIntervalMinutes] = useState<string>(() =>
    intervalToMinutes(enduserSettings?.delayedReadingIntervalInMS)
  );
  const [isIntervalDirty, setIsIntervalDirty] = useState(false);

  // ── Section: Invalidation Reasons ──────────────────────
  const [reasonInput, setReasonInput] = useState("");
  const [invalidationReasons, setInvalidationReasons] = useState<string[]>(
    () => org?.observationInvalidationReasons ?? []
  );
  const [isReasonsDirty, setIsReasonsDirty] = useState(false);

  // ── Queries ───────────────────────────────────────────────────────────────
  const vitalConfigurationsQuery = useTellescopeInfiniteQuery({
    model: "vital_configurations",
    method: "getSome",
    request: {},
    select: (data) => data.pages.flat(),
  });

  const programsConfigQuery = useTellescopeInfiniteQuery({
    model: "configurations",
    method: "getSome",
    request: { filter: { type: "Vital Programs" } },
    select: (data) => data.pages.flat(),
  });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const updateOrgMutation = useTellescopeMutation({
    model: "organizations",
    method: "updateOne",
  });

  const createVitalConfigMutation = useTellescopeMutation({
    model: "vital_configurations",
    method: "createOne",
  });

  const updateVitalConfigMutation = useTellescopeMutation({
    model: "vital_configurations",
    method: "updateOne",
  });

  const deleteVitalConfigMutation = useTellescopeMutation({
    model: "vital_configurations",
    method: "deleteOne",
  });

  const createProgramsMutation = useTellescopeMutation({
    model: "configurations",
    method: "createOne",
  });

  const updateProgramsMutation = useTellescopeMutation({
    model: "configurations",
    method: "updateOne",
  });

  const vitalConfigurations = vitalConfigurationsQuery.data ?? [];

  const programsConfigRecord = programsConfigQuery.data?.[0];

  const parsedPrograms: VitalPrograms = programsConfigRecord?.value
    ? parseVitalProgramConfigurationValue(programsConfigRecord.value)
    : { programs: [] };

  // ── Auto-save toggles ──────────────
  const handleToggle = (field: "showFullVitalsTab" | "requireObservationInvalidationReason", value: boolean) => {
    if (!org) return;
    updateOrgMutation.mutate({
      id: org.id,
      settings: {
        endusers: {
          [field]: value,
        },
      },
    });
  };

  // ── Interval section ──────────────────────────────────────────────────────
  const handleDelayedIntervalChange = (value: string) => {
    setDelayedReadingIntervalMinutes(value);
    setIsIntervalDirty(true);
  };

  const handleSaveInterval = async () => {
    if (!org) return;
    const minutes = parseFloat(delayedReadingIntervalMinutes);
    await updateOrgMutation.mutateAsync({
      id: org.id,
      settings: {
        endusers: {
          delayedReadingIntervalInMS: !isNaN(minutes) && minutes >= 0 ? minutes * MS_PER_MINUTE : undefined,
        },
      },
    });
    setIsIntervalDirty(false);
  };

  const handleCancelInterval = () => {
    setDelayedReadingIntervalMinutes(intervalToMinutes(org?.settings?.endusers?.delayedReadingIntervalInMS));
    setIsIntervalDirty(false);
  };

  // ── Reasons section ───────────────────────────────────────────────────────
  const handleAddReason = (reason: string) => {
    const trimmed = reason.trim();
    if (!trimmed || invalidationReasons.includes(trimmed)) return;
    setInvalidationReasons((prev) => [...prev, trimmed]);
    setReasonInput("");
    setIsReasonsDirty(true);
  };

  const handleRemoveReason = (reason: string) => {
    setInvalidationReasons((prev) => prev.filter((r) => r !== reason));
    setIsReasonsDirty(true);
  };

  const handleSaveReasons = async () => {
    if (!org) return;
    await updateOrgMutation.mutateAsync({
      id: org.id,
      observationInvalidationReasons: invalidationReasons,
      _options: { replaceObjectFields: true },
    });
    setIsReasonsDirty(false);
  };

  const handleCancelReasons = () => {
    setInvalidationReasons(org?.observationInvalidationReasons ?? []);
    setReasonInput("");
    setIsReasonsDirty(false);
  };

  // ── Vital configs section ─────────────────────────────────────────────────
  const handleDeleteVitalConfig = (id: string) => {
    deleteVitalConfigMutation.mutate(id);
  };

  const handleSavePrograms = async (programs: VitalProgram[]) => {
    const value = JSON.stringify({ programs } satisfies VitalPrograms);
    if (programsConfigRecord?.id) {
      await updateProgramsMutation.mutateAsync({ id: programsConfigRecord.id, value });
    } else {
      await createProgramsMutation.mutateAsync({ type: "Vital Programs", value });
    }
    setView("settings");
  };

  const handleSaveVitalConfig = async (data: {
    title: string;
    unit: string;
    ranges: TellescopeModelFromName<"vital_configurations">["ranges"];
  }) => {
    if (selectedConfig) {
      await updateVitalConfigMutation.mutateAsync({
        id: selectedConfig.id,
        ...data,
        _options: { replaceObjectFields: true },
      });
    } else {
      await createVitalConfigMutation.mutateAsync(data);
    }
    setSelectedConfig(null);
    setView("settings");
  };

  const openEditConfig = (config: TellescopeModelFromName<"vital_configurations">) => {
    setSelectedConfig(config);
    setView("edit-config");
  };

  const openAddConfig = () => {
    setSelectedConfig(null);
    setView("add-config");
  };

  const cancelSubView = () => {
    setSelectedConfig(null);
    setView("settings");
  };

  return {
    view,
    setView,
    selectedConfig,
    org,
    // Auto-save toggles
    showFullVitalsTab: enduserSettings?.showFullVitalsTab ?? false,
    requireObservationInvalidationReason: enduserSettings?.requireObservationInvalidationReason ?? false,
    handleToggle,
    isTogglesaving: updateOrgMutation.isPending,
    // Interval section
    delayedReadingIntervalMinutes,
    isIntervalDirty,
    handleDelayedIntervalChange,
    handleSaveInterval,
    handleCancelInterval,
    isSavingInterval: updateOrgMutation.isPending,
    // Reasons section
    reasonInput,
    setReasonInput,
    invalidationReasons,
    isReasonsDirty,
    handleAddReason,
    handleRemoveReason,
    handleSaveReasons,
    handleCancelReasons,
    isSavingReasons: updateOrgMutation.isPending,
    // Configs list section
    vitalConfigurations,
    handleDeleteVitalConfig,
    // Sub-views
    parsedPrograms,
    handleSavePrograms,
    handleSaveVitalConfig,
    openEditConfig,
    openAddConfig,
    cancelSubView,
    isSavingPrograms: createProgramsMutation.isPending || updateProgramsMutation.isPending,
    isSavingVitalConfig: createVitalConfigMutation.isPending || updateVitalConfigMutation.isPending,
  };
};
